import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Star as StarIcon,
  Storefront as StorefrontIcon,
  Category as CategoryIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      maxWidth: "900px",
      width: "90%",
      maxHeight: "80vh",
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: theme.spacing(1),
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "center",
  },
  searchField: {
    flex: 1,
  },
  categorySelect: {
    minWidth: 150,
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  productsGrid: {
    maxHeight: "400px",
    overflowY: "auto",
    padding: theme.spacing(1),
  },
  productCard: {
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  selectedCard: {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.selected,
  },
  productImage: {
    height: 120,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  noImage: {
    height: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[500],
  },
  productPrice: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  featuredChip: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: 200,
    color: theme.palette.text.secondary,
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

const ProductSelectionModal = ({ 
  open, 
  onClose, 
  onProductSelect, 
  onCatalogSend,
  ticketId 
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentTab, setCurrentTab] = useState(0); // 0: All Products, 1: Featured
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);

  const fetchProducts = useCallback(async () => {
    if (!ticketId) return;
    
    setLoading(true);
    try {
      const params = {
        searchParam: searchTerm,
        category: selectedCategory,
        featured: currentTab === 1 ? "true" : "",
        active: "true",
      };

      const response = await api.get(`/tickets/${ticketId}/catalog/products`, { params });
      setProducts(response.data.products);
      setCount(response.data.count);
      setHasMore(response.data.hasMore);
    } catch (error) {
      console.error("Error fetching products:", error);
      toastError("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  }, [ticketId, searchTerm, selectedCategory, currentTab]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get("/catalog/categories");
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchProducts();
      fetchCategories();
    }
  }, [open, fetchProducts, fetchCategories]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (open) {
        fetchProducts();
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, selectedCategory, currentTab, fetchProducts, open]);

  const handleProductClick = (product) => {
    setSelectedProduct(selectedProduct?.id === product.id ? null : product);
  };

  const handleSendProduct = async () => {
    if (!selectedProduct) return;

    try {
      await onProductSelect(selectedProduct);
      onClose();
      toastUtils.success("Produto enviado com sucesso!", "Catálogo");
    } catch (error) {
      console.error("Error sending product:", error);
      toastError("Erro ao enviar produto");
    }
  };

  const handleSendCatalog = async () => {
    try {
      await onCatalogSend();
      onClose();
      toastUtils.success("Catálogo enviado com sucesso!", "Catálogo");
    } catch (error) {
      console.error("Error sending catalog:", error);
      toastError("Erro ao enviar catálogo");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  const renderProductCard = (product) => (
    <Grid item xs={12} sm={6} md={4} key={product.id}>
      <Card
        className={`${classes.productCard} ${
          selectedProduct?.id === product.id ? classes.selectedCard : ""
        }`}
        onClick={() => handleProductClick(product)}
      >
        <Box position="relative">
          {product.imageUrl ? (
            <CardMedia
              className={classes.productImage}
              image={product.imageUrl}
              title={product.name}
            />
          ) : (
            <Box className={classes.noImage}>
              <StorefrontIcon fontSize="large" />
            </Box>
          )}
          {product.featured && (
            <Chip
              icon={<StarIcon />}
              label="Destaque"
              size="small"
              className={classes.featuredChip}
            />
          )}
        </Box>
        <CardContent>
          <Typography variant="h6" component="h3" noWrap>
            {product.name}
          </Typography>
          <Typography variant="body2" color="textSecondary" noWrap>
            {product.description}
          </Typography>
          <Typography variant="h6" className={classes.productPrice}>
            {formatPrice(product.price)}
          </Typography>
          {product.category && (
            <Chip
              icon={<CategoryIcon />}
              label={product.category}
              size="small"
              variant="outlined"
            />
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">
          {i18n.t("productSelection.title")}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box className={classes.tabsContainer}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab 
              label={
                <Badge badgeContent={count} color="primary">
                  Todos os Produtos
                </Badge>
              } 
            />
            <Tab label="Produtos em Destaque" />
          </Tabs>
        </Box>

        <Box className={classes.searchContainer}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar produtos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" />,
            }}
          />
          <FormControl className={classes.categorySelect}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.name}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.productsGrid}>
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : products.length > 0 ? (
            <Grid container spacing={2}>
              {products.map(renderProductCard)}
            </Grid>
          ) : (
            <Box className={classes.emptyState}>
              <StorefrontIcon fontSize="large" />
              <Typography variant="h6">
                Nenhum produto encontrado
              </Typography>
              <Typography variant="body2">
                Tente ajustar os filtros de busca
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Box className={classes.actionButtons}>
          <Button onClick={onClose} color="default">
            Cancelar
          </Button>
          <Button
            onClick={handleSendCatalog}
            color="primary"
            variant="outlined"
            startIcon={<StorefrontIcon />}
          >
            Enviar Catálogo
          </Button>
          <Button
            onClick={handleSendProduct}
            color="primary"
            variant="contained"
            disabled={!selectedProduct}
            startIcon={<SendIcon />}
          >
            Enviar Produto
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSelectionModal;